import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { UserPreferences } from '../models/userPreferences.model';
import { PmUnitEnum } from '../enums/pmUnit.enum';
import { TemperatureUnitEnum } from '../enums/temperatureUnit.enum';
import { ParameterTypeEnum } from '../enums/parameterType.enum';
import { ConcentrationParameterTypes, GasParameterTypes } from '../shared/constants/parameterTypeGroups.constants';
import { FlowrateUnitValue, GasUnitValue, RainfallUnitValue, WindspeedUnitValue } from '../enums/unitValues.enum';
import { ParameterUnitService } from './parameter-unit.service';
import { Units, UnitSymbols } from '../enums/units.enum';

const UG_PER_M3_TO_MG_PER_M3 = 0.001;
const MG_PER_M3_TO_UG_PER_M3 = 1000;
const CM2_TO_FT2_CONVERSION = 929.0304;
const CELSIUS_TO_FAHRENHEIT_MULTIPLIER = 1.8;
const CELSIUS_TO_FAHRENHEIT_OFFSET = 32;
const FAHRENHEIT_TO_CELSIUS_MULTIPLIER = 5 / 9;
const ML_PER_MIN_TO_L_PER_MIN = 1000;
const PPB_TO_PPM = 1000;
const MM_PER_HR_TO_IN_PER_HR = 25.4;
const MPH_TO_MPS = 0.44704;
const FPS_TO_MPS = 0.3048;
const KPH_TO_MPS_DIVISOR = 3.6;
const KNOTS_TO_MPS = 0.514444;
const MPS_TO_MPH = 0.44704;
const MPS_TO_FPS = 0.3048;
const MPS_TO_KPH = 3.6;
const MPS_TO_KNOTS = 0.514444;
const HOURS_TO_SECONDS = 3600;
const MINUTES_TO_SECONDS = 60;
const CM_TO_FT = 30.48;
const SQFT_TO_SQCM = 929.0304;

@Injectable({
    providedIn: 'root'
})
export class ValueConversionService extends ParameterUnitService {
    constructor(protected authService: AuthService) {
        super(authService)
    }

    get preferences(): UserPreferences {
        const currentUser = this.authService.getCurrentUser();
        return currentUser.preferences;
    }

    getParameterValueFromUserPreferences(parameterValue, parameterType, preferences: UserPreferences = this.preferences) {
        if (preferences?.pmUnitId === PmUnitEnum.MgPerM3 &&
            ConcentrationParameterTypes.includes(parameterType)) {
            parameterValue = this.convertUgPerM3ToMgPerM3(parameterValue);
        } else if (preferences?.temperatureUnitId === TemperatureUnitEnum.Fahrenheit &&
            (parameterType === ParameterTypeEnum.Temperature || parameterType === ParameterTypeEnum.FeelsLike)) {
            parameterValue = this.convertCelsiusToFahrenheit(parameterValue);
        } else if (GasParameterTypes.includes(parameterType)) {
            return preferences.gasUnit === GasUnitValue.PPM
                ? this.convertPpbToPpm(parameterValue)
                : parameterValue;
        } else if (parameterType === ParameterTypeEnum.WindSpeed) {
            switch (preferences.windSpeedUnit) {
                case WindspeedUnitValue.MilesPerHour:
                    return this.convertMpsToMph(parameterValue);
                case WindspeedUnitValue.FeetPerSecond:
                    return this.convertMpsToFps(parameterValue);
                case WindspeedUnitValue.KmPerhour:
                    return this.convertMpsToKph(parameterValue);
                case WindspeedUnitValue.Knot:
                    return this.convertMpsToKnots(parameterValue);
                default:
                    return parameterValue;
            }
        } else if (parameterType === ParameterTypeEnum.Rainfall) {
            return preferences.rainfallUnit === RainfallUnitValue.InchPerHour ?
                this.convertMmPerHrToInPerHr(parameterValue) : parameterValue
        } else if (parameterType === ParameterTypeEnum.FlowRate) {
            return preferences.flowRateUnit === FlowrateUnitValue.LPerMinute ?
                this.convertMlPerMinToLPerMin(parameterValue) : parameterValue
        }
        return parameterValue;
    }

    convertUserPreferredValueToBase(parameterValue, parameterType): number {
        const preferences = this.preferences;
        if (ConcentrationParameterTypes.includes(parameterType)) {
            return preferences?.pmUnitId === PmUnitEnum.MgPerM3
                ? this.convertMgPerM3ToUgPerM3(parameterValue)
                : parameterValue;
        } else if (parameterType === ParameterTypeEnum.Temperature || parameterType === ParameterTypeEnum.FeelsLike) {
            return preferences?.temperatureUnitId === TemperatureUnitEnum.Fahrenheit
                ? this.convertFahrenheitToCelsius(parameterValue)
                : parameterValue;
        } else if (GasParameterTypes.includes(parameterType)) {
            return preferences.gasUnit === GasUnitValue.PPM
                ? this.convertPpmToPpb(parameterValue)
                : parameterValue;
        } else if (parameterType === ParameterTypeEnum.WindSpeed) {
            switch (preferences.windSpeedUnit) {
                case WindspeedUnitValue.MilesPerHour:
                    return this.convertMphToMps(parameterValue);
                case WindspeedUnitValue.FeetPerSecond:
                    return this.convertFpsToMps(parameterValue);
                case WindspeedUnitValue.KmPerhour:
                    return this.convertKphToMps(parameterValue);
                case WindspeedUnitValue.Knot:
                    return this.convertKnotsToMps(parameterValue);
                default:
                    return parameterValue;
            }
        } else if (parameterType === ParameterTypeEnum.Rainfall) {
            return preferences.rainfallUnit === RainfallUnitValue.InchPerHour ?
                this.convertInPerHrToMmPerHr(parameterValue) : parameterValue
        } else if (parameterType === ParameterTypeEnum.FlowRate) {
            return preferences.flowRateUnit === FlowrateUnitValue.LPerMinute ?
                this.convertLPerMinToMlPerMin(parameterValue) : parameterValue
        }
        return parameterValue;
    }

    getParameterValueAsPerAgentUnit(value, unit: Units) {
        if (unit === Units.MgPerM3) {
            value = this.convertUgPerM3ToMgPerM3(value);
        } else if (unit === Units.Ppm) {
            value = this.convertPpbToPpm(value);
        } else if (unit === Units.UgPerCm2) {
            value = this.convertUgPerFt2ToUgPerCm2(value);
        }
        return value;
    }

    getParameterUnitSymbol(unit: Units) {
        return UnitSymbols[unit];
    }

    convertAgentUnitValueToBase(value, unit: Units) {
        if (unit === Units.MgPerM3) {
            value = this.convertMgPerM3ToUgPerM3(value);
        } else if (unit === Units.Ppm) {
            value = this.convertPpmToPpb(value);
        } else if (unit === Units.UgPerCm2) {
            value = this.convertUgPerCm2ToUgPerFt2(value);
        }
        return value;
    }


    convertUgPerM3ToMgPerM3(value) {
        return value * UG_PER_M3_TO_MG_PER_M3;
    }

    convertMgPerM3ToUgPerM3(value) {
        return value * MG_PER_M3_TO_UG_PER_M3;
    }

    convertUgPerFt2ToUgPerCm2(value: number): number {
        return value / CM2_TO_FT2_CONVERSION;
    }

    convertUgPerCm2ToUgPerFt2(value: number): number {
        return value * CM2_TO_FT2_CONVERSION;
    }

    convertCelsiusToFahrenheit(value) {
        return value * CELSIUS_TO_FAHRENHEIT_MULTIPLIER + CELSIUS_TO_FAHRENHEIT_OFFSET;
    }

    convertFahrenheitToCelsius(value) {
        return (value - CELSIUS_TO_FAHRENHEIT_OFFSET) * FAHRENHEIT_TO_CELSIUS_MULTIPLIER;
    }

    convertMlPerMinToLPerMin(value) {
        return value / ML_PER_MIN_TO_L_PER_MIN;
    }

    convertLPerMinToMlPerMin(value) {
        return value * ML_PER_MIN_TO_L_PER_MIN;
    }

    convertPpbToPpm(value) {
        return value / PPB_TO_PPM;
    }

    convertPpmToPpb(value) {
        return value * PPB_TO_PPM;
    }

    convertMmPerHrToInPerHr(value) {
        return value / MM_PER_HR_TO_IN_PER_HR;
    }

    convertInPerHrToMmPerHr(value) {
        return value * MM_PER_HR_TO_IN_PER_HR;
    }

    convertMphToMps(value) {
        return value * MPH_TO_MPS;
    }

    convertFpsToMps(value) {
        return value * FPS_TO_MPS;
    }

    convertKphToMps(value) {
        return value / KPH_TO_MPS_DIVISOR;
    }

    convertKnotsToMps(value) {
        return value * KNOTS_TO_MPS;
    }

    convertMpsToMph(value) {
        return value / MPS_TO_MPH;
    }

    convertMpsToFps(value) {
        return value / MPS_TO_FPS;
    }

    convertMpsToKph(value) {
        return value * MPS_TO_KPH;
    }

    convertMpsToKnots(value) {
        return value / MPS_TO_KNOTS;
    }

    convertHoursToSeconds(value) {
        return value * HOURS_TO_SECONDS;
    }

    convertMinutesToSeconds(value) {
        return value * MINUTES_TO_SECONDS;
    }

    convertSecondsToHours(value) {
        return value / HOURS_TO_SECONDS;
    }

    convertSecondsToMinutes(value) {
        return value / MINUTES_TO_SECONDS;
    }

    convertCmToFt(value: number) {
        return value / CM_TO_FT;
    }

    convertFtToCm(value: number) {
        return value * CM_TO_FT;
    }

    convertSqFtToSqCm(value: number) {
        return value * SQFT_TO_SQCM;
    }

    convertSqCmToSqFt(value: number) {
        return value / SQFT_TO_SQCM;
    }

    getAveragingPeriod(averagingPeriod: number): string {
        if (averagingPeriod < 60 * 60) {
            const minutes = this.convertSecondsToMinutes(averagingPeriod);
            const unit = minutes === 1 ? 'min' : 'mins';
            return `${minutes} ${unit}`;
        } else {
            const hours = this.convertSecondsToHours(averagingPeriod);
            const unit = hours === 1 ? 'hour' : 'hours';
            return `${hours} ${unit}`;
        }
    }
}
